import { RouteNames } from '@/ui/types/RouteNames'

const withBaseTherapistBaseRoute = <T extends string>(path: T) =>
  `/therapist${path}` as const
const withBaseTherapistClientRoute = <T extends string>(path: T) =>
  `/therapist/patient${path}` as const

export const therapistDynamicRoutes = {
  profileInfo: <T extends string>(idClient: T) =>
    withBaseTherapistBaseRoute(`/profile-info/${idClient}`),
  therapistSchedule: <T extends string>(paramDate?: T) =>
    withBaseTherapistBaseRoute(`/schedule/${paramDate ?? ''}`),
  ticketDetails: <T extends string>(ticketId: T) =>
    withBaseTherapistBaseRoute(`/ticket-details/${ticketId}`),
  groupDetails: <T extends string>(groupId: T) =>
    withBaseTherapistBaseRoute(`/groups?filter=&ALL&groupId=${groupId}`),

  schedule: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/schedule`),
  gallery: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/gallery`),
  galleryItem: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/gallery/:id`),
  galleryFolder: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/folder/:id`),
  config: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/config-client`),
  groupsFeed: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/groups-feed`),
  medicalSheet: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/medical-sheet`),
  patientTicket: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/ticket`),
  documents: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/documents`),
  food: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/food`),
  restriction: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/restriction`),
  liquids: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/liquids`),
  bathroom: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/bathroom`),
  sleep: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/sleep`),
  patientBelongs: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/patientBelongs`),
  remedy: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/remedy`),
  bodyInspection: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/body-inspection`),

  interferingBehavior: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/interfering-behavior`),
  comments: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/comments`),
  caaClient: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/caa`),
  photosClient: <T extends string>(patientId: T) =>
    withBaseTherapistClientRoute(`/${patientId}/photos`)
} as const

export const therapistRouteNames = {
  home: withBaseTherapistBaseRoute(''),
  fileEditor: withBaseTherapistBaseRoute('/fileEditor'),
  profile: withBaseTherapistBaseRoute('/profile'),
  frequenciesManager: withBaseTherapistBaseRoute(
    '/managed-patients-frequencies'
  ),

  feed: withBaseTherapistBaseRoute('/feed'),
  chat: withBaseTherapistBaseRoute('/chat'),
  groups: withBaseTherapistBaseRoute('/groups'),
  tasks: withBaseTherapistBaseRoute('/tasks'),
  ticket: withBaseTherapistBaseRoute('/ticket'),
  explore: withBaseTherapistBaseRoute('/explore'),
  ticketDetails: therapistDynamicRoutes.ticketDetails(':ticketId'),
  ticketChat: withBaseTherapistBaseRoute('/ticket-chat'),
  session: withBaseTherapistBaseRoute('/session'),
  caa: withBaseTherapistBaseRoute('/caa'),
  interferingBehavior: withBaseTherapistBaseRoute('/interfering-behavior'),
  groupDetails: withBaseTherapistBaseRoute('/groups/:groupId'),
  groupDrive: withBaseTherapistBaseRoute('/groups/:groupId/drive'),
  groupDriveItem: withBaseTherapistBaseRoute('/groups/:groupId/drive/:id'),
  groupDriveFolder: withBaseTherapistBaseRoute(
    '/groups/:groupId/drive/folder/:id'
  ),
  taskDetails: withBaseTherapistBaseRoute('/tasks/:id'),
  postDetails: withBaseTherapistBaseRoute('/post-details/:id/:commentId?'),
  documentsForTherapist: withBaseTherapistBaseRoute('/documents'),
  patients: withBaseTherapistBaseRoute('/patients'),
  entryPatient: withBaseTherapistBaseRoute('/patient/:patientId/*'),

  schedule: therapistDynamicRoutes.therapistSchedule(':paramDate?'),
  profileInfo: therapistDynamicRoutes.profileInfo(':idClient'),

  interferingBehaviorClient:
    therapistDynamicRoutes.interferingBehavior(':patientId'),
  comments: therapistDynamicRoutes.comments(':patientId'),
  caaClient: therapistDynamicRoutes.caaClient(':patientId'),
  photosClient: therapistDynamicRoutes.photosClient(':patientId'),
  clientSchedule: therapistDynamicRoutes.schedule(':patientId'),
  gallery: therapistDynamicRoutes.gallery(':patientId'),
  galleryItem: therapistDynamicRoutes.galleryItem(':patientId'),
  galleryFolder: therapistDynamicRoutes.galleryFolder(':patientId'),
  configClient: therapistDynamicRoutes.config(':patientId'),
  groupsFeed: therapistDynamicRoutes.groupsFeed(':patientId'),
  medicalSheet: therapistDynamicRoutes.medicalSheet(':patientId'),
  documents: therapistDynamicRoutes.documents(':patientId'),
  food: therapistDynamicRoutes.food(':patientId'),
  restriction: therapistDynamicRoutes.restriction(':patientId'),
  liquids: therapistDynamicRoutes.liquids(':patientId'),
  bathroom: therapistDynamicRoutes.bathroom(':patientId'),
  sleep: therapistDynamicRoutes.sleep(':patientId'),
  patientBelongs: therapistDynamicRoutes.patientBelongs(':patientId'),
  remedy: therapistDynamicRoutes.remedy(':patientId'),
  bodyInspection: therapistDynamicRoutes.bodyInspection(':patientId'),
  patientTicket: therapistDynamicRoutes.patientTicket(':patientId')
} as const satisfies RouteNames
